// El sonido de "listo" de una generación, con UNA guarda para toda la app —
// ADR-2026-09-06-un-solo-listo-por-racimo.
//
// El sonido es por CORRIDA: cada rama de generación lo dispara una vez, después de que
// resuelve su poll. El problema es que los modos dejan correr hasta tres tandas a la vez
// a propósito (`tandaVaAlPanel` saltea el candado de doble click para poder seguir
// generando con una en curso), así que tres que terminan juntas disparaban tres veces el
// mismo clic, encimado. No es un aviso tres veces más claro: es ruido.
//
// La guarda es de MÓDULO y no de componente: las corridas que se pisan viven en closures
// distintos —y hasta en pantallas distintas, el chat y un modo— así que un ref por
// componente no las vería. Una sola instancia para toda la app es justamente el punto.

/**
 * Cuánto dura el racimo, en ms.
 *
 * Dos segundos, no trescientos milisegundos: lo que hay que colapsar es "terminaron
 * juntas", y entre la primera y la última de una tanda de tres pasan cientos de ms. Y no
 * diez segundos: dos tandas separadas por cinco segundos son dos eventos y merecen dos
 * avisos — la guarda está para el racimo, no para racionar el sonido.
 */
export const VENTANA_ANTI_RACIMO_MS = 2000;

/**
 * ¿Suena, o es la segunda de un racimo que ya sonó?
 *
 * Pura y con el reloj por parámetro: es la única parte con una decisión adentro, así que
 * es la que se testea.
 *
 * @param ahora epoch en ms.
 * @param ultimo cuándo sonó por última vez, o `null` si nunca.
 */
export function debeSonar(
  ahora: number,
  ultimo: number | null,
  ventanaMs: number = VENTANA_ANTI_RACIMO_MS,
): boolean {
  if (ultimo === null) return true;
  // `ahora < ultimo` es el reloj yendo para atrás (ajuste de hora, suspensión). Sin
  // este caso, un 'último' en el futuro dejaría el sonido mudo hasta que el reloj lo
  // alcance — que puede ser horas.
  if (ahora < ultimo) return true;
  return ahora - ultimo >= ventanaMs;
}

let ultimoSonido: number | null = null;

/**
 * Reproduce el "listo", como mucho una vez por racimo.
 *
 * Reemplaza a los `new Audio('/images/clicksound.m4a').play()` sueltos que había en cada
 * rama de generación. No confundir con `useCompleteSound`, que es otra cosa: ese resuelve
 * el DESBLOQUEO del audio en iOS desde el gesto del usuario y lo usa la pantalla de carga
 * full-screen. Acá el audio ya está desbloqueado —la corrida arrancó con un tap— y lo
 * único que se agrega es la guarda.
 */
export function sonarListo(): void {
  const ahora = Date.now();
  if (!debeSonar(ahora, ultimoSonido)) return;
  ultimoSonido = ahora;
  try {
    // Varios navegadores bloquean el audio fuera de un gesto del usuario. Que no suene
    // no puede tirar abajo el final de una generación que el usuario ya pagó.
    void new Audio('/images/clicksound.m4a').play();
  } catch {
    /* silent */
  }
}

export default sonarListo;
